<script lang="tsx">
import type { UnwrapNestedRefs } from "vue";
import { defineComponent, getCurrentInstance, reactive, ref, toRefs } from "vue";
import type { Component, ComponentInternalInstance, CSSProperties, ExtractPropTypes, PropType, VNode } from "vue";
import { OnItemChangeTypeParams } from "./type";
import type { DragValueType, MouseStateType, OnClickType, OnItemChangeType } from "./type";
import cloneDeep from "lodash-es/cloneDeep";
import $ from "jquery";

import { buildProps } from "../../utils/props";

const initState: MouseStateType = {
  s: {
    x: 0,
    y: 0,
  },
  m: {
    x: 0,
    y: 0,
  },
  n: {
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  },
  keyDown: false,
  target: "drag-bar",
};

const defaultOnItemChange: OnItemChangeType = (
  config: OnItemChangeTypeParams
): void => {
  config;
};

const defaultOnClick: OnClickType = (e: MouseEvent): void => {
  e;
};

// drag组件props定义
const dragProps = buildProps({
  unit: {
    type: String,
    default: "px",
  },
  value: {
    type: Object as PropType<DragValueType>,
    default: (): DragValueType => {
      return {
        left: 0,
        top: 0,
        width: 100,
        height: 100,
      };
    },
  },
  className: {
    type: String,
    default: "",
  },
  boxStyle: {
    type: Object as PropType<CSSProperties>,
    default: () => ({}),
  },
  wrapWidth: {
    type: Number,
    default: 0,
  },

  minWidth: {
    type: Number,
    default: 5,
  },
  minHeight: {
    type: Number,
    default: 5,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    default: "DragBox" + new Date().valueOf(),
  },
  disabledScale: {
    type: Boolean,
    default: false,
  },
  minLeft: {
    type: Number,
    default: 0,
  },
  minTop: {
    type: Number,
    default: 0,
  },
  onItemChange: {
    type: Function as PropType<OnItemChangeType>,
    default: defaultOnItemChange,
  },
  onClick: {
    type: Function as PropType<OnClickType>,
    default: defaultOnClick,
  },
});

type DragPropsType = ExtractPropTypes<typeof dragProps>;

// 自定义拖拽组件
export default defineComponent({
  name: "Drag",
  components: {},
  model: {
    prop: "value", // 要存在于props
    event: "changeDragValue", // 当组件的值发生改变时要emit的事件名
  },
  props: dragProps,
  setup(props: DragPropsType, contents: any) {
    let refs: any;
    const dom: any = (el: any) => {
      refs = el;
    };

    const state: UnwrapNestedRefs<DragValueType> = reactive({
      left: props.value.left,
      top: props.value.top,
      width: props.value.width,
      height: props.value.height,
    });
    let mouseState: UnwrapNestedRefs<MouseStateType> = reactive(
      cloneDeep(initState)
    );

    // methods
    const saveShapeStyle = (newStyle: DragValueType): void => {
      Object.keys(newStyle).map((key: string) => {
        type DragValueKeyType = keyof typeof newStyle;
        const nowItemVal: string | number = newStyle[key as DragValueKeyType];
        refs.style[key] =
          typeof nowItemVal == "number" ? nowItemVal + "px" : nowItemVal;
      });
      state.left = newStyle.left;
      state.top = newStyle.top;
      state.width = newStyle.width;
      state.height = newStyle.height;
    };
    const filterShape = (type: string, turnStyle: any): void => {
      // eslint-disable-next-line no-undef
      const jqDom: JQuery = $(refs as HTMLElement);

      switch (type) {
        case "leftRight": {
          if (
            !props.wrapWidth ||
            (turnStyle.left + turnStyle.width < props.wrapWidth &&
              turnStyle.width >= props.minWidth &&
              turnStyle.left >= props.minLeft)
          ) {
            saveShapeStyle({
              width: turnStyle.width,
              left: turnStyle.left,
              top: state.top,
              height: state.height,
            });
            props.onItemChange({
              name: props.name,
              data: getNowShapeState(),
            });
            contents.emit("changeDragValue", getNowShapeState());
          }
          break;
        }
        case "topBottom": {
          if (
            !props.wrapWidth ||
            (turnStyle.top + turnStyle.height <
              Boolean(jqDom.parent().height()) &&
              turnStyle.height >= props.minHeight &&
              turnStyle.top >= props.minTop)
          ) {
            saveShapeStyle({
              height: turnStyle.height,
              top: turnStyle.top,
              width: state.width,
              left: state.left,
            });

            props.onItemChange({
              name: props.name,
              data: getNowShapeState(),
            });
            contents.emit("changeDragValue", getNowShapeState());
          }
          break;
        }
        case "bevel": {
          if (
            !props.wrapWidth ||
            (turnStyle.top + turnStyle.height <
              Boolean(jqDom.parent().height()) &&
              turnStyle.height >= props.minHeight &&
              turnStyle.top >= props.minTop &&
              turnStyle.left + turnStyle.width < props.wrapWidth &&
              turnStyle.width >= props.minWidth &&
              turnStyle.left >= props.minLeft)
          ) {
            saveShapeStyle({
              width: turnStyle.width,
              left: turnStyle.left,
              height: turnStyle.height,
              top: turnStyle.top,
            });
            props.onItemChange({
              name: props.name,
              data: getNowShapeState(),
            });
            contents.emit("changeDragValue", getNowShapeState());
          }
          break;
        }

        case "dragBar": {
          const newObj: DragValueType = {
            top: state.top,
            left: state.left,
            width: state.width,
            height: state.height,
          };

          if (props.wrapWidth) {
            if (turnStyle.top < props.minTop) {
              newObj.top = props.minTop;
            } else if (
              turnStyle.top + turnStyle.height >
              (jqDom.parent().height() || 0)
            ) {
              newObj.top = (jqDom.parent().height() || 0) - turnStyle.height;
            } else {
              newObj.top = turnStyle.top;
            }

            if (turnStyle.left < props.minLeft) {
              newObj.left = props.minLeft;
            } else if (turnStyle.left + turnStyle.width > props.wrapWidth) {
              newObj.left = props.wrapWidth - turnStyle.width;
            } else {
              newObj.left = turnStyle.left;
            }
          } else {
            newObj.top = turnStyle.top;
            newObj.left = turnStyle.left;
          }
          saveShapeStyle(newObj);
          props.onItemChange({ name: props.name, data: getNowShapeState() });
          contents.emit("changeDragValue", getNowShapeState());
          break;
        }
      }
    };

    const getNowShapeState = (obj: DragValueType = state): DragValueType => {
      const nowShape: DragValueType = {
        left: obj.left,
        top: obj.top,
        width: obj.width,
        height: obj.height,
      };
      return nowShape;
    };
    // 移动到左边方法
    const moveLeftFn = (): void => {
      let width = Number(mouseState.n.width);
      let left = Number(mouseState.n.left);
      const lang = mouseState.s.x - mouseState.m.x;
      width = Number(width) + lang;
      left = Number(left) - lang;
      filterShape("leftRight", { left: left, width: width });
    };

    // 移动到右边方法
    const moveRightFn = (): void => {
      let width = Number(mouseState.n.width);
      const left = Number(mouseState.n.left);
      const lang: number = mouseState.s.x - mouseState.m.x;
      width = Number(width) - lang;
      filterShape("leftRight", { left: left, width: width });
    };
    // 移动到上边方法
    const moveTopFn = (): void => {
      let height = Number(mouseState.n.height);
      let top = Number(mouseState.n.top);
      const lang: number = mouseState.s.y - mouseState.m.y;
      height = height + lang;
      top = top - lang;
      filterShape("topBottom", { height: height, top: top });
    };
    // 移动到下边方法
    const moveBottomFn = (): void => {
      let height = Number(mouseState.n.height);
      const top = Number(mouseState.n.top);
      const lang: number = mouseState.s.y - mouseState.m.y;
      height = height - lang;
      filterShape("topBottom", { top: top, height: height });
    };
    // 移动到左上边方法
    const moveTopLeftFn = (): void => {
      let height = Number(mouseState.n.height);
      let width = Number(mouseState.n.width);
      let left = Number(mouseState.n.left);
      let top = Number(mouseState.n.top);
      const langY: number = mouseState.s.y - mouseState.m.y;
      const bi: number = width / height;
      const lastWidth: number = width;

      height = height + langY;
      width = height * bi;
      top = top - langY;
      // left = left - langX;
      left = left + lastWidth - width;
      filterShape("bevel", { left, top, height, width });
    };
    // 移动到右上边方法
    const moveTopRightFn = (): void => {
      let height = Number(mouseState.n.height);
      let width = Number(mouseState.n.width);
      const left = Number(mouseState.n.left);
      let top = Number(mouseState.n.top);
      const langY: number = mouseState.s.y - mouseState.m.y;
      const bi: number = width / height;

      height = height + langY;
      width = height * bi;
      top = top - langY;

      filterShape("bevel", { left, top, height, width });
    };
    // 移动到左下边方法
    const moveBottomLeftFn = (): void => {
      let height = Number(mouseState.n.height);
      let width = Number(mouseState.n.width);
      let left = Number(mouseState.n.left);
      const top = Number(mouseState.n.top);
      const langY: number = mouseState.s.y - mouseState.m.y;
      const bi: number = width / height;
      const lastWidth: number = width;

      height = height - langY;
      width = height * bi;
      //  top = top - langY;
      left = left + lastWidth - width;
      filterShape("bevel", { left, top, height, width });
    };
    // 移动到右下边方法
    const moveBottomRightFn = (): void => {
      let height = Number(mouseState.n.height);
      let width = Number(mouseState.n.width);
      const left = Number(mouseState.n.left);
      const top = Number(mouseState.n.top);
      const langY: number = mouseState.s.y - mouseState.m.y;

      const bi: number = width / height;
      height = height - langY;
      width = height * bi;
      // top = top - langY;
      //  left = left - langX;
      filterShape("bevel", { left, top, height, width });
    };
    // 移动到中间bar方法
    const moveDragBarFn = (): void => {
      const left = Number(mouseState.n.left);
      const top = Number(mouseState.n.top);
      const height: number | string = mouseState.n.height;
      const width: number | string = mouseState.n.width;
      const langX: number = mouseState.m.x - mouseState.s.x;
      const langY: number = mouseState.m.y - mouseState.s.y;

      filterShape("dragBar", {
        top: top + langY,
        left: left + langX,
        width: width,
        height: height,
      });
    };

    function bindMove(e: MouseEvent): void {
      e.stopPropagation();
      e.preventDefault();
      onMouseMoveFn(e);
    }
    function bindUp(e: MouseEvent): void {
      e.stopPropagation();
      e.preventDefault();

      onMouseUpFn(e);
    }
    function onMouseDownFn(e: MouseEvent): void {
      if (props.disabled) {
        return;
      }
      const domEle: Element = e.target as Element;
      e.stopPropagation();
      e.preventDefault();
      document.addEventListener("mousemove", bindMove, false);
      document.addEventListener("mouseup", bindUp, false);
      mouseState.s = {
        x: e.pageX,
        y: e.pageY,
      };

      mouseState.target = domEle.className ? domEle.className : "";

      mouseState.n = {
        width: state.width,
        height: state.height,
        left: state.left,
        top: state.top,
      };

      mouseState.keyDown = true;
    }
    function onMouseMoveFn(e: MouseEvent): void {
      const moveFuncMap = {
        moveLeftFn,
        moveRightFn,
        moveTopFn,
        moveBottomFn,
        moveTopLeftFn,
        moveTopRightFn,
        moveBottomLeftFn,
        moveBottomRightFn,
        moveDragBarFn,
      };
      type FuncMapType = keyof typeof moveFuncMap;
      const nowFunc = moveFuncMap[`move${mouseState.target}Fn` as FuncMapType];
      e.stopPropagation();
      e.preventDefault();

      if (mouseState.keyDown) {
        mouseState.m = {
          x: e.pageX,
          y: e.pageY,
        };

        if (nowFunc) {
          nowFunc();
        }
      }
    }
    function onMouseUpFn(e: MouseEvent): void {
      if (mouseState.keyDown) {
        document.removeEventListener("mousemove", bindMove, false);
        document.removeEventListener("mouseup", bindUp, false);
        mouseState = cloneDeep(initState);
      }
    }
    function onMouseClickFunc(e: MouseEvent): void {
      props.onClick(e);
      contents.emit("click", e);
    }
    return {
      ...toRefs(state),
      barStyle: {},
      dom,
      mouseState,
      onMouseDownFn,
      onMouseClickFunc,
    };
  },

  render(_ctx: any, _cache: any): VNode {
    const { dom, boxStyle, barStyle, onMouseClickFunc, disabled } = _ctx;

    const { zIndex, ...otherStyle } = boxStyle;
    const setStyle: CSSProperties = {
      left: _ctx.left + _ctx.unit,
      top: _ctx.top + _ctx.unit,
      width: _ctx.width + _ctx.unit,
      height: _ctx.height + _ctx.unit,
    };
    const disabledShow = disabled ? "none" : "block";
    return (
      <div
        ref={dom}
        style={{ ...boxStyle, ...setStyle }}
        onMousedown={_ctx.onMouseDownFn}
        class={"drag-box " + _ctx.className}
      >
        <div class="cont-in">
          {_ctx.$slots && _ctx.$slots.default ? _ctx.$slots.default : null}
        </div>

        {_ctx.disabledScale ? null : (
          <div
            class="TopLeft"
            style={{ zIndex: zIndex + 2, display: disabledShow }}
          ></div>
        )}
        {_ctx.disabledScale ? null : (
          <div
            class="TopRight"
            style={{ zIndex: zIndex + 2, display: disabledShow }}
          ></div>
        )}
        {_ctx.disabledScale ? null : (
          <div
            class="BottomLeft"
            style={{ zIndex: zIndex + 2, display: disabledShow }}
          ></div>
        )}
        {_ctx.disabledScale ? null : (
          <div
            class="BottomRight"
            style={{ zIndex: zIndex + 2, display: disabledShow }}
          ></div>
        )}

        {_ctx.disabledScale ? null : (
          <div
            class="Top"
            style={{ zIndex: zIndex + 1, display: disabledShow }}
          ></div>
        )}
        {_ctx.disabledScale ? null : (
          <div
            class="Right"
            style={{ zIndex: zIndex + 1, display: disabledShow }}
          ></div>
        )}
        {_ctx.disabledScale ? null : (
          <div
            class="Bottom"
            style={{ zIndex: zIndex + 1, display: disabledShow }}
          ></div>
        )}
        {_ctx.disabledScale ? null : (
          <div
            class="Left"
            style={{ zIndex: zIndex + 1, display: disabledShow }}
          ></div>
        )}
        <div
          class="DragBar"
          onClick={onMouseClickFunc}
          style={{ zIndex: zIndex, display: disabledShow, ...barStyle }}
        ></div>
      </div>
    );
  },
});
</script>

<style lang="scss" scoped>
$borderColor: #79c2fd;
.drag-box {
  position: absolute !important;
  border: 1px solid $borderColor;
  z-index: 5;
  &.item-point {
    background: rgba(0, 167, 255, 0.2);
    //  border:1px solid #79c2fd;
    text-align: center;
    .top {
      border-top: 1px solid $borderColor;
    }
    .left {
      border-left: 1px solid $borderColor;
    }
    .right {
      border-right: 1px solid $borderColor;
    }
    .bottom {
      border-bottom: 1px solid $borderColor;
    }
  }
  .cont-in {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    //  overflow :hidden;
  }
  .DragBar {
    position: absolute;
    height: 100%;
    width: 100%;
    z-index: 1;
    cursor: move;

    top: 0;
    left: 0;
  }
  .Top {
    width: 100%;
    height: 3px;
    top: 0;
    left: 0;
    position: absolute;
    z-index: 2;
    cursor: n-resize;
  }
  .Right {
    height: 100%;
    width: 3px;
    top: 0;
    right: 0;
    position: absolute;
    z-index: 2;
    cursor: e-resize;
  }
  .Bottom {
    width: 100%;
    height: 3px;
    bottom: 0;
    left: 0;
    position: absolute;
    z-index: 2;
    cursor: n-resize;
  }
  .Left {
    height: 100%;
    width: 3px;
    top: 0;
    left: 0;
    position: absolute;
    z-index: 2;
    cursor: e-resize;
  }

  .TopLeft {
    width: 16px;
    height: 16px;
    top: -8px;
    left: -8px;
    position: absolute;
    z-index: 2;
    cursor: nw-resize;
    background: $borderColor;
    pointer-events: auto;
    border-radius: 100%;
  }
  .TopRight {
    height: 16px;
    width: 16px;
    top: -8px;
    left: 100%;
    margin-left: -8px;
    position: absolute;
    z-index: 2;
    cursor: ne-resize;
    pointer-events: auto;
    background: $borderColor;
    border-radius: 100%;
  }
  .BottomLeft {
    width: 16px;
    height: 16px;
    bottom: -8px;
    left: -8px;
    position: absolute;
    z-index: 2;
    cursor: sw-resize;
    pointer-events: auto;
    background: $borderColor;
    border-radius: 100%;
  }
  .BottomRight {
    height: 16px;
    width: 16px;
    bottom: -8px;
    left: 100%;
    margin-left: -8px;
    position: absolute;
    z-index: 2;
    cursor: se-resize;
    pointer-events: auto;
    background: $borderColor;
    border-radius: 100%;
  }
}
</style>
